import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";

const FIELD = "w-full rounded-md border bg-background p-2 text-sm";
const CHECK = "h-4 w-4 shrink-0";
export default function MarketplaceLanding() {
  const { token } = useParams();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", state: "FL",
    age_18_plus: false, privacy_ack: false, share_opt_in: false,
    sms_opt_in: false, call_opt_in: false, website: ""
  });
  const set = (key, value) => setForm(old => ({ ...old, [key]: value }));
  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/public/lead-marketplace/${token}`).then(({data}) => {
      if (active) setConfig(data);
    }).catch(() => { if(active) setError("This information request is not currently available."); })
      .finally(() => { if(active) setLoading(false); });
    return () => { active = false; };
  }, [token]);

  const submit = async (event) => {
    event.preventDefault();
    if (busy || !config) return;
    setBusy(true);
    setError("");
    try {
      const search = new URLSearchParams(window.location.search);
      const attribution = Object.fromEntries(["utm_source", "utm_medium", "utm_campaign"]
        .filter(key => search.has(key)).map(key => [key, search.get(key).slice(0, 150)]));
      await api.post(`/public/lead-marketplace/${token}/leads`, {
        ...form, form_token: config.form_token
      }, { params: attribution });
      setDone(true);
      setForm({first_name: "",last_name: "",email: "",phone: "",state:"FL",
        age_18_plus:false,privacy_ack:false,share_opt_in:false,sms_opt_in:false,call_opt_in:false,website:""});
    } catch(e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Your request could not be submitted. Please reload and try again.");
    } finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-xl space-y-5 rounded-xl border bg-card p-6">
        {loading ? <p>Loading information request…</p> : error && !config ? <p role="alert">{error}</p> :
          done ? <div role="status"><h1 className="text-xl font-semibold">Request received</h1>
            <p className="mt-2 text-sm text-muted-foreground">Thank you. Your information request has been received. This does not create an insurance policy or guarantee contact or coverage.</p>
          </div> : config ? <>
            <header>
              <p className="text-sm font-medium text-muted-foreground">{config.seller_legal_name}</p>
              <h1 className="mt-2 text-2xl font-bold">{config.campaign_name || "Final Expense information request"}</h1>
              <p className="mt-2 text-sm text-muted-foreground">Request general information about Final Expense life insurance in Florida. We are a marketing company, not an insurance carrier.</p>
            </header>
            <form onSubmit={submit} className="space-y-4" data-testid="marketplace-public-form">
              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm">First name<input className={FIELD} required maxLength={80}
                  autoComplete="given-name" value={form.first_name} onChange={e => set("first_name",e.target.value)}/></label>
                <label className="text-sm">Last name<input className={FIELD} required maxLength={80}
                  autoComplete="family-name" value={form.last_name} onChange={e => set("last_name",e.target.value)}/></label>
              </div>
              <label className="block text-sm">Email<input className={FIELD} required type="email" maxLength={254}
                autoComplete="email" value={form.email} onChange={e => set("email",e.target.value)}/></label>
              <label className="block text-sm">Phone<input className={FIELD} required type="tel" maxLength={40}
                autoComplete="tel" value={form.phone} onChange={e => set("phone",e.target.value)}/></label>
              <p className="text-sm text-muted-foreground">State: Florida</p>
              <div className="rounded-lg border p-3 text-sm">
                <p className="whitespace-pre-wrap">{config.disclosure_text}</p>
                <a href={config.privacy_policy_url} target="_blank" rel="noreferrer"
                  className="mt-2 inline-block underline">Privacy policy</a>
              </div>
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className={CHECK} required checked={form.age_18_plus} onChange={e => set("age_18_plus",e.target.checked)}/>
                I confirm I am 18 years of age or older.
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className={CHECK} required checked={form.privacy_ack} onChange={e => set("privacy_ack",e.target.checked)}/>
                I acknowledge the information-use disclosure and privacy policy above.
              </label>
              {config.buyer_names?.length > 0 && config.buyer_sharing_text && <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className={CHECK} checked={form.share_opt_in} onChange={e => set("share_opt_in",e.target.checked)}/>
                {config.buyer_sharing_text}
              </label>}
              {config.sms_consent_text && <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className={CHECK} checked={form.sms_opt_in} onChange={e => set("sms_opt_in",e.target.checked)}/>
                {config.sms_consent_text}
              </label>}
              {config.call_consent_text && <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className={CHECK} checked={form.call_opt_in} onChange={e => set("call_opt_in",e.target.checked)}/>
                {config.call_consent_text}
              </label>}
              <div className="hidden" aria-hidden="true"><label>Website<input tabIndex={-1} autoComplete="off"
                value={form.website} onChange={e => set("website",e.target.value)}/></label></div>
              {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
              <button type="submit" disabled={busy || !form.privacy_ack || !form.age_18_plus}
                className="w-full rounded-lg bg-primary px-4 py-3 font-medium text-primary-foreground disabled:opacity-50">
                {busy ? "Submitting…" : "Send information request"}
              </button>
              <p className="text-xs text-muted-foreground">Submitting does not bind you to buy insurance. Optional marketing permissions are never required.</p>
            </form>
          </> : null}
      </div>
    </main>
  );
}
